(() => {
  /* =========================================================
     PHOTOCARD BINDER — LOCAL-STATE SELECT + BATCHED CART SYNC

     WHY THIS SHAPE
     The previous engine fired /cart/add.js + /cart.js on every single card
     click and blocked all further clicks (state.toggling) until both returned.
     On a UK connection that's ~150ms and feels fine. From the US it's 600ms-1s
     per card, so a customer picking four cards quickly had most of their clicks
     silently swallowed — the binder looked broken.

     Now: clicks only touch a local Set and repaint immediately. Nothing is ever
     blocked. A debounced flush (SYNC_DEBOUNCE_MS after clicking stops) diffs the
     Set against the real cart and sends ONE batched add and ONE batched update.
     Four cards = 2 requests instead of 8.

     Safety rails that come with that trade:
       - postWithRetry backs off on 429/503 rather than dropping the change
       - reconcile() un-ticks anything the cart rejected (sold out mid-click)
       - the checkout link FLUSHES before navigating, so nobody reaches checkout
         inside the debounce window with an empty cart
       - only variants that exist on this page are ever removed, so items added
         elsewhere in the store are never touched

     MARKET AWARENESS — TWO SHIPPING MECHANICS
       window.BINDER_SHIPPING.mode
         'count' (ROW) -> cards selected vs freeThreshold (2)
         'value' (US)  -> selection subtotal vs freeMinAmount (5000 = $50.00)
       window.BINDER_CURRENCY  checkout currency for formatting

     A count rule cannot pay for the US lane: the envelope costs roughly three
     times the UK one, so the promo has to be earned by basket VALUE. Both
     bridges fall back to the old hardcoded behaviour if the template hasn't
     been updated, so this file is safe to deploy on its own.

     ---------------------------------------------------------
     SELECT ALL LABELS — CHANGED IN THIS REVISION
     The arrow and tick are gone: "Select all" and "All selected", plain.

     These strings are written on EVERY render by updateGroupTabs(), so they
     override whatever the Liquid template rendered. That means the template's
     own label has to be changed to match, or the arrow flashes on first paint
     and then vanishes when the first render runs. In
     page.photocard-binder.liquid:

         Select all &rarr;     ->     Select all

     Both places or neither.
     ========================================================= */

  const SYNC_DEBOUNCE_MS = 1800;  // quiet period after the last click.
                                  // Raised from 900 for transatlantic latency:
                                  // gives a US customer time to finish picking
                                  // (and to undo a mis-click for free) before
                                  // anything is sent. The checkout flush and
                                  // pagehide handlers cover the wider window.
  const POLL_INTERVAL_MS = 25000; // stock poll cadence (was 15000)
  const POLL_STAGGER_MS  = 200;   // gap between per-product poll requests

  const SELECTOR = {
    tile:               ".binder-card[data-variant-id]",
    addBtn:             ".binder-add[data-variant-id]",

    tray:               "#binderTray",
    trayToggle:         "[data-tray-toggle]",
    trayBody:           "[data-tray-body]",
    trayList:           "[data-tray-list]",
    trayCount:          "[data-tray-count]",
    traySubtotal:       "[data-tray-subtotal]",

    counterSelected:    "[data-binder-selected]",
    counterTotalPrice:  "[data-binder-total-price]",
    counterOrigPrice:   "[data-binder-original-price]",
    counterCloses:      "[data-binder-closes]",
    promoText:          "[data-binder-promo]",
    shipNudge:          "[data-binder-ship-nudge]",

    clearAllBtn:        "[data-binder-clearall]",
    checkoutLink:       ".binder-checkout, .binder-tray__checkout",
    bottomBar:          "[data-binder-bottom-bar]",

    groupTab:           ".binder-group",
    groupRemaining:     "[data-group-remaining]",
    groupTotal:         "[data-group-total]",
    groupPrice:         "[data-group-price]",
    groupSelectAll:     "[data-group-selectall]",
    groupStatus:        "[data-group-status]"
  };

  /* Button copy in one place. The tags are a fixed width in CSS
     (.binder-group__selectall / .binder-group__status share a min-width), so a
     longer string here can overflow the sticker rather than widen it — check
     the draft theme if you lengthen either of these. */
  const LABEL = {
    selectAll:   "Select all",
    allSelected: "All selected"
  };

  const state = {
    cart:      null,
    selected:  new Set(),   // LOCAL truth for what's ticked on screen
    syncing:   false,
    pending:   false,       // a change arrived while a sync was in flight
    dirty:     false,       // local Set differs from the server cart
    syncTimer: null,
    lastSyncAt: 0,          // when our own flush last finished
    soldOut:   new Set()    // variants the server refused (sold out)
  };

  /* How long after our own sync to ignore cart events from elsewhere. The
     theme's mini-cart re-broadcasts its own refresh once it redraws, and that
     rebroadcast carries no source flag — so without this the binder treats its
     own echo as an external change and rebuilds the selection from a cart that
     may still be catching up. */
  const EXTERNAL_COOLDOWN_MS = 1500;

  const stockState = {};
  const claimTimes = {};

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /* ──────────────────────────────────────────────────────────
     HELPERS
     ────────────────────────────────────────────────────────── */

  /* The free-shipping rule for this market, read once.

     mode 'count' -> compare card count against `count`
     mode 'value' -> compare selection subtotal (minor units) against `amount`

     A template that predates hb_ship_mode emits no mode, which older builds
     meant as 'count' — so that is the fallback, with the old default of 2. */
  const shipRule = (() => {
    const cfg  = window.BINDER_SHIPPING || {};
    const mode = cfg.mode === "value" ? "value" : "count";

    const count = Number.isFinite(Number(cfg.freeThreshold)) && Number(cfg.freeThreshold) > 0
      ? Number(cfg.freeThreshold)
      : 2;

    const amount = Number.isFinite(Number(cfg.freeMinAmount))
      ? Number(cfg.freeMinAmount)
      : 0;

    // Value mode with no amount configured is a misconfiguration, not a rule.
    // Fall back to count so the binder still says something true.
    const active = mode === "value" ? amount > 0 : count > 0;

    return {
      mode: mode === "value" && amount > 0 ? "value" : "count",
      count,
      amount,
      amountText: cfg.freeMinAmountText || "",
      active
    };
  })();

  /* Kept as its own function because the promo branches still reference a card
     count for their own wording. */
  function getFreeShipThreshold() {
    return shipRule.count;
  }

  /* Currency-aware money. try/catch because a bad currency code would otherwise
     throw inside Intl and take the whole bottom bar down.

     The fallback no longer hardcodes a pound sign — printing "£50.00" to a US
     customer is worse than printing "50.00 USD", because one of those is a
     wrong price and the other is only an ugly one. */
  function moneyFromCartJS(priceInPence) {
    const amount   = Number(priceInPence || 0) / 100;
    const cfg      = window.BINDER_CURRENCY || {};
    const currency = cfg.active || "GBP";
    const locale   = cfg.locale || "en-GB";

    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
    } catch (e) {
      return currency === "GBP" ? `£${amount.toFixed(2)}` : `${amount.toFixed(2)} ${currency}`;
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll("&",  "&amp;")
      .replaceAll("<",  "&lt;")
      .replaceAll(">",  "&gt;")
      .replaceAll('"',  "&quot;")
      .replaceAll("'",  "&#039;");
  }

  function getPromo() {
    return window.BINDER_PROMO || {};
  }

  function normaliseMoneyToPence(value) {
    if (value == null || value === "") return 0;
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  /* Every variant rendered on this page. Used so the sync only ever removes
     binder cards — anything the customer added elsewhere in the store stays. */
  function binderVariantIds() {
    const ids = new Set();
    $$(SELECTOR.tile).forEach((tile) => {
      const vid = tile.getAttribute("data-variant-id");
      if (vid) ids.add(String(vid));
    });
    return ids;
  }

  function isSelectable(tile) {
    const btn = $(SELECTOR.addBtn, tile);
    return !!btn && btn.tagName === "BUTTON" && !btn.classList.contains("is-disabled");
  }

  /* ──────────────────────────────────────────────────────────
     CART API
     ────────────────────────────────────────────────────────── */

  async function fetchCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  /* Retries only on rate limiting / temporary unavailability. A 422 means the
     item genuinely can't be added (sold out), so retrying would just stall. */
  async function postWithRetry(url, body, attempts = 3) {
    let delay = 400;

    for (let i = 0; i < attempts; i++) {
      let res;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(body)
        });
      } catch (networkErr) {
        if (i === attempts - 1) return null;
        await sleep(delay);
        delay *= 2;
        continue;
      }

      if (res.ok) return res;
      if (res.status === 429 || res.status === 503) {
        await sleep(delay);
        delay *= 2;
        continue;
      }
      return res;
    }
    return null;
  }

  function dispatchCartRefreshEvents() {
    const detail = { cart: state.cart, source: "binder" };
    [
      "cart:refresh", "cart:updated", "cart:update",
      "theme:cart:refresh", "theme:cart:updated", "ajaxCart:refresh"
    ].forEach((name) => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent(name, { detail }));
    });
  }

  /* ──────────────────────────────────────────────────────────
     SELECTION STATE
     Everything below reads state.selected, NOT the cart. That's what makes
     clicking instant — the cart catches up in the background.
     ────────────────────────────────────────────────────────── */

  function isSelected(variantId) {
    return state.selected.has(String(variantId));
  }

  function getSelectedQty() {
    return state.selected.size;
  }

  /* Price for one tile, in minor units.

     PREFERS data-price-cents, which Liquid renders as a raw integer.

     The old path parsed data-price — a FORMATTED money string — by stripping
     every non-digit and re-parsing. That works for "£12.00" and survives a
     thousands comma, but it silently destroys any locale that uses a comma as
     the DECIMAL separator: "12,00 €" strips to "1200", parses as 1200, and
     becomes 120000 minor units. A hundredfold overcharge on screen, in exactly
     the EU markets we ship to most.

     String parsing is kept only as the fallback for a template that predates
     data-price-cents, and it is the reason that attribute was added. */
  function tilePricePence(tile) {
    const cents = tile.getAttribute("data-price-cents");
    if (cents != null && cents !== "") {
      const n = parseInt(cents, 10);
      if (!Number.isNaN(n)) return n;
    }

    const raw = tile.getAttribute("data-price");
    if (!raw) return 0;
    const num = parseFloat(raw.replace(/[^0-9.]/g, ""));
    return Number.isNaN(num) ? 0 : Math.round(num * 100);
  }

  function getSelectedOriginalTotal() {
    let sum = 0;
    $$(SELECTOR.tile).forEach((tile) => {
      const vid = tile.getAttribute("data-variant-id");
      if (vid && isSelected(vid)) sum += tilePricePence(tile);
    });
    return sum;
  }

  /* Does the cart we last fetched actually contain everything that is ticked?

     state.dirty going false only means "the sync finished" — NOT "the cart
     agrees with the screen". Shopify's /cart.js can lag the add that produced
     it, so for a moment the cart holds fewer lines than the customer has
     selected. Any total derived from it is then too low.

     This is the guard that stops the free-shipping rule un-meeting itself:
     the cart is only trusted as a source of truth once it covers the whole
     selection. */
  function cartCoversSelection() {
    if (!state.cart?.items) return false;

    const inCart = new Set(
      state.cart.items.filter((i) => i.quantity > 0).map((i) => String(i.variant_id))
    );

    for (const id of state.selected) {
      if (!inCart.has(id)) return false;
    }
    return true;
  }

  /* Discounted total is only knowable from the server, so we use the cart when
     it is caught up — meaning clean AND covering the selection — and fall back
     to the on-screen total otherwise.

     Without cartCoversSelection() this number could drop while the selection
     only grew, which made the bar announce "$12 to free shipping" a beat after
     it had said "unlocked", then correct itself. A total that feeds a
     threshold must never move backwards while the basket moves forwards. */
  function getSelectedFinalTotal() {
    if (state.dirty || !cartCoversSelection()) return getSelectedOriginalTotal();

    return state.cart.items.reduce((sum, item) => {
      if (!isSelected(item.variant_id)) return sum;
      const line =
        item.final_line_price ??
        ((item.final_price ?? item.price ?? 0) * (item.quantity || 1));
      return sum + line;
    }, 0);
  }

  function getSelectedSavings() {
    return Math.max(0, getSelectedOriginalTotal() - getSelectedFinalTotal());
  }

  function hasFreeGiftLine() {
    if (state.dirty || !cartCoversSelection()) return false;
    return state.cart.items.some((item) => {
      if (!isSelected(item.variant_id)) return false;
      const line =
        item.final_line_price ??
        ((item.final_price ?? item.price ?? 0) * (item.quantity || 1));
      return line === 0;
    });
  }

  /* Published so other scripts — the float counter especially — can read exact
     numbers instead of parsing them back out of formatted strings. Rewritten on
     every render, so it is always in step with what is on screen. */
  function publishSelection() {
    window.BINDER_SELECTION = {
      count:     getSelectedQty(),
      subtotal:  getSelectedFinalTotal(),
      original:  getSelectedOriginalTotal(),
      ids:       [...state.selected],
      shipMode:  shipRule.mode,
      shipMet:   isFreeShippingMet(),
      dirty:     state.dirty
    };
  }

  /* ──────────────────────────────────────────────────────────
     FREE SHIPPING RULE

     Value mode compares the DISCOUNTED subtotal, because that is what Shopify's
     shipping rate is evaluated against. See the caveat in updateShipNudge.
     ────────────────────────────────────────────────────────── */

  function freeShippingShortfall() {
    if (!shipRule.active) return null;

    if (shipRule.mode === "value") {
      return Math.max(0, shipRule.amount - getSelectedFinalTotal());
    }
    return Math.max(0, shipRule.count - getSelectedQty());
  }

  function isFreeShippingMet() {
    if (!shipRule.active) return false;
    if (getSelectedQty() === 0) return false;
    return freeShippingShortfall() === 0;
  }

  /* ──────────────────────────────────────────────────────────
     SYNC — debounced, batched, retrying
     ────────────────────────────────────────────────────────── */

  function scheduleSync(delay = SYNC_DEBOUNCE_MS) {
    state.dirty = true;
    clearTimeout(state.syncTimer);
    state.syncTimer = setTimeout(() => { flushSync(); }, delay);
  }

  async function flushSync() {
    clearTimeout(state.syncTimer);

    if (state.syncing) { state.pending = true; return; }
    state.syncing = true;

    try {
      if (!state.cart) state.cart = await fetchCart();

      const mine   = binderVariantIds();
      const inCart = new Set(
        (state.cart.items || [])
          .filter((i) => i.quantity > 0)
          .map((i) => String(i.variant_id))
      );

      const adds = [...state.selected].filter((id) => !inCart.has(id));
      // Only ever remove cards that belong to this binder page.
      const removes = [...inCart].filter((id) => mine.has(id) && !state.selected.has(id));

      /* addOutcome tells reconcile how much to trust a missing card.
           "ok"      every add succeeded — anything missing is almost certainly
                     a lagging /cart.js read, so verify before condemning.
           "partial" the batch failed and we retried per item; `rejected` names
                     exactly which ones the server refused.
           "unknown" the request never completed (network). Condemn nothing. */
      let addOutcome = "ok";
      const rejected = new Set();

      if (adds.length) {
        const res = await postWithRetry("/cart/add.js", {
          items: adds.map((id) => ({ id: Number(id), quantity: 1 }))
        });

        if (res === null) {
          // Never reached the server, or gave up retrying. We have no idea what
          // the cart contains, so nothing may be marked as claimed.
          addOutcome = "unknown";
          console.warn("[Binder] add never completed — leaving selection intact");

        } else if (!res.ok) {
          /* /cart/add.js is ATOMIC: one unavailable line fails the whole
             request and adds NOTHING. Batching four cards where one has gone
             therefore loses all four, and the old reconcile stamped CLAIMED on
             every one of them.

             So on failure, re-add one at a time. Only the card the server
             actually refuses is treated as gone; the rest go in as normal. */
          addOutcome = "partial";
          console.warn("[Binder] batch add refused — retrying per card");

          for (const id of adds) {
            const one = await postWithRetry("/cart/add.js", {
              items: [{ id: Number(id), quantity: 1 }]
            });

            if (one === null) {
              addOutcome = "unknown"; // lost confidence again; condemn nothing
              break;
            }
            if (!one.ok) rejected.add(id);
          }
        }
      }

      if (removes.length) {
        const updates = {};
        removes.forEach((id) => { updates[id] = 0; });
        await postWithRetry("/cart/update.js", { updates });
      }

      state.cart  = await fetchCart();
      state.dirty = false;

      await reconcile(addOutcome, rejected);
      dispatchCartRefreshEvents();

    } catch (e) {
      console.error("[Binder] sync failed:", e);
      // Leave state.dirty true so the next click retries rather than silently
      // dropping the customer's selection.
    } finally {
      state.syncing = false;
      state.lastSyncAt = Date.now();
      if (state.pending) {
        state.pending = false;
        scheduleSync(0);
      }
    }
  }

  function cartVariantIds(cart) {
    return new Set(
      (cart?.items || []).filter((i) => i.quantity > 0).map((i) => String(i.variant_id))
    );
  }

  /* Anything the customer ticked that the cart genuinely refused gets un-ticked
     and stamped CLAIMED.

     Stamping is DESTRUCTIVE and effectively permanent — the card is greyed, the
     button is replaced with a dead div, and the id joins state.soldOut so it can
     never be picked again this session. It must therefore never fire on
     suspicion. Absence from one cart read is not proof: /cart.js lags the add
     that produced it, a batch add fails atomically, a request can vanish.

     So a card is only condemned when either the server named it outright, or it
     is still missing after repeated confirming reads. */
  const RECONCILE_VERIFY_DELAYS = [500, 1200];

  async function reconcile(addOutcome = "ok", rejected = new Set()) {
    if (!state.cart?.items) return;

    // The request never completed, so the cart state is unknown. Leave the
    // selection exactly as the customer left it and let the next sync settle it.
    if (addOutcome === "unknown") { render(); return; }

    let missing = [...state.selected].filter((id) => !cartVariantIds(state.cart).has(id));
    if (!missing.length) { render(); return; }

    // Cards the server explicitly refused during the per-card retry are proven
    // gone — no verification needed.
    const confirmed = new Set([...missing].filter((id) => rejected.has(id)));
    let unproven = missing.filter((id) => !rejected.has(id));

    /* Everything else gets the benefit of the doubt. Re-read the cart a couple
       of times before condemning: a lagging read catches up within a few
       hundred milliseconds, whereas a genuinely claimed card stays missing. */
    for (const wait of RECONCILE_VERIFY_DELAYS) {
      if (!unproven.length) break;

      await sleep(wait);

      // A click during the wait means the selection moved on; abandon this pass
      // rather than acting on a snapshot that is already out of date.
      if (state.dirty || state.syncing) { render(); return; }

      try {
        state.cart = await fetchCart();
      } catch (e) {
        console.warn("[Binder] verify read failed — condemning nothing");
        render();
        return;
      }

      const nowInCart = cartVariantIds(state.cart);
      unproven = unproven.filter((id) => !nowInCart.has(id));
    }

    unproven.forEach((id) => confirmed.add(id));

    if (!confirmed.size) { render(); return; }

    confirmed.forEach((id) => markCardAsSoldOut(id));
    console.warn(
      `[Binder] ${confirmed.size} card(s) confirmed claimed before checkout:`,
      [...confirmed]
    );

    render();
  }

  /* ──────────────────────────────────────────────────────────
     FREE SHIPPING NUDGE

     Lives in its own element so it never competes with a product promo for the
     same slot. updatePromoText no longer prints shipping copy at all — exactly
     one shipping message is on screen at a time.

     COUNT MODE is now dynamic: the taped-note bar is wide enough not to reflow,
     and "add 1 more card" is the line that actually converts. (The old copy
     said "Add 4 cards for free shipping" even when the customer already had
     three, which was simply wrong.)

     VALUE MODE is dynamic too, because it has to be. "Spend $50" tells a
     customer nothing about how close they are, and the entire reason a $50
     threshold earns its keep is that a three-card basket lands just short of
     it. Give the element a min-width and font-variant-numeric: tabular-nums in
     CSS so the digits do not jitter.
     ────────────────────────────────────────────────────────── */

  const SHIP_MIN_FILL = 10; // % — see below

  /* Last markup written, so an unchanged nudge is never rewritten.

     render() runs on every click, every sync and every external cart event.
     Reassigning innerHTML each time tears down and rebuilds the inner span
     even when the words are identical, which the browser paints as a flicker
     on the busiest element in the bar. */
  let lastShipHTML = null;

  /* "free uk shipping — sorted!" outside the US, "free usa shipping — sorted!"
     inside it. Reads the label Liquid already rendered so the two can never
     disagree; falls back to a neutral line if the bridge is old. */
  function shipUnlockedCopy() {
    const label = (window.BINDER_SHIPPING && window.BINDER_SHIPPING.label) || "";
    return label
      ? `${label.toLowerCase()} — sorted!`
      : "free shipping — sorted!";
  }

  function updateShipNudge() {
    const el = $(SELECTOR.shipNudge);
    if (!el) return;

    const hide = () => {
      if (lastShipHTML !== null) {
        el.textContent = "";
        lastShipHTML = null;
      }
      el.hidden = true;
      el.removeAttribute("data-state");
      el.style.removeProperty("--ship-progress");
    };

    const paint = (html, dataState) => {
      if (html !== lastShipHTML) {
        el.innerHTML = html;
        lastShipHTML = html;
      }
      el.setAttribute("data-state", dataState);
      el.hidden = false;
    };

    if (!shipRule.active || getSelectedQty() === 0) return hide();

    const shortfall = freeShippingShortfall();

    /* Fill percentage for the mint highlight.

       NOTE: the CSS progress fill was removed — the nudge is a flat highlighter
       block now and .binder-bottom-bar__ship-nudge::before is set to
       content: none. This property is therefore inert. It is left in place
       because it costs nothing and re-enabling the meter is a CSS-only change.

       Floored at SHIP_MIN_FILL because when the fill IS drawn it doubles as the
       meter: a single cheap card is about 13% of a $50 rule and a £5 card is
       less, and below roughly a tenth the tint reads as a rendering glitch
       rather than as early progress. A visible sliver says "started". */
    const target = shipRule.mode === "value" ? shipRule.amount : shipRule.count;
    const have   = shipRule.mode === "value" ? getSelectedFinalTotal() : getSelectedQty();
    const pct    = target > 0
      ? Math.min(100, Math.max(SHIP_MIN_FILL, (have / target) * 100))
      : 0;
    el.style.setProperty("--ship-progress", pct + "%");

    /* Always emit the inner span. The fill is an absolutely positioned
       ::before, and a positioned pseudo-element paints ABOVE the parent's bare
       text nodes — so unwrapped text would sit behind the tint and disappear.
       The span gives the copy its own positioned context to sit in front.

       Two spans: an amount that takes the serif figure treatment and a label
       that takes the handwritten treatment, so the nudge keeps an internal
       hierarchy instead of becoming one flat line. */
    const wrap = (html) =>
      `<span class="binder-bottom-bar__ship-text">${html}</span>`;

    const amt = (v) =>
      `<span class="binder-bottom-bar__ship-amt">${v}</span>`;

    if (shortfall === 0) {
      return paint(wrap(shipUnlockedCopy()), "applied");
    }

    /* innerHTML is safe here: every part is generated by this file from a
       number, never from customer input. escapeHtml on the money string is
       belt and braces in case a currency ever formats with an entity. */
    if (shipRule.mode === "value") {
      paint(
        wrap(`${amt(escapeHtml(moneyFromCartJS(shortfall)))}<span>to free shipping</span>`),
        "progress"
      );
    } else {
      paint(
        wrap(
          `<span>add</span>${amt(shortfall)}` +
          `<span>more card${shortfall === 1 ? "" : "s"} for free shipping</span>`
        ),
        "progress"
      );
    }
  }

  /* ──────────────────────────────────────────────────────────
     PROMO TEXT
     ────────────────────────────────────────────────────────── */

  function getActivePromo() {
    const selectedTiles = $$(".binder-card.is-selected");

    for (const tile of selectedTiles) {
      const group = tile.closest(".binder-group");
      if (!group) continue;

      const label = group.dataset.productPromoLabel;
      if (!label || !label.trim()) continue;

      return {
        type:       group.dataset.productPromoType || "",
        label:      label.trim(),
        threshold:  group.dataset.productPromoThreshold || 0,
        buyQty:     group.dataset.productPromoBuyQty || 0,
        getQty:     group.dataset.productPromoGetQty || 0,
        percentOff: group.dataset.productPromoPercentOff || 0,
        amountOff:  group.dataset.productPromoAmountOff || 0,
        source:     "product"
      };
    }

    return { ...getPromo(), source: "collection" };
  }

  function updatePromoText() {
    const el = $(SELECTOR.promoText);
    if (!el) return;

    const promo         = getActivePromo();
    const type          = String(promo.type || "").trim();
    const qty           = getSelectedQty();
    const originalTotal = getSelectedOriginalTotal();
    const savings       = getSelectedSavings();

    const textSpan = el.querySelector(".binder-bottom-bar__promo-text");
    const target   = textSpan || el;

    const hide = () => {
      el.classList.add("is-hidden");
      el.setAttribute("aria-hidden", "true");
      el.removeAttribute("data-state");
    };

    const show = (message, dataState) => {
      if (target.textContent !== message) target.textContent = message;
      el.setAttribute("data-state", dataState);
      el.classList.remove("is-hidden");
      el.removeAttribute("aria-hidden");
    };

    if (qty === 0) return hide();

    // No product promo: the shipping nudge already has its own element, so
    // this slot stays empty rather than duplicating the message.
    if (!type) return hide();

    if (type === "percentage" || type === "product_percent") {
      const percent  = Number(promo.percentOff || 0);
      const label    = promo.label || `${percent}% off`;
      const isMobile = window.innerWidth < 640;

      if (savings > 0) {
        show(isMobile ? label : `${label} — saving ${moneyFromCartJS(savings)}`, "applied");
      } else {
        show(label, "available");
      }
      return;
    }

    if (type === "bxgy") {
      const buyQty       = Number(promo.buyQty || 0);
      const progressWrap = document.querySelector("[data-binder-progress]");
      const progressFill = document.querySelector("[data-binder-progress-fill]");
      const isMobile     = window.innerWidth < 640;

      const setProgress = (pct, visible) => {
        if (progressFill) progressFill.style.width = `${Math.min(100, Math.max(0, pct))}%`;
        if (progressWrap) progressWrap.style.display = (!isMobile && visible) ? "block" : "none";
      };

      if (hasFreeGiftLine() || savings > 0) {
        setProgress(100, false);
        show(promo.label || "Offer applied", "applied");
      } else if (buyQty > 0 && qty < buyQty) {
        setProgress((qty / buyQty) * 100, true);
        show(`Add ${buyQty} cards to get 1 free`, "progress");
      } else if (qty >= buyQty) {
        setProgress(100, true);
        show("Choose your free card below", "available");
      } else {
        setProgress(0, false);
        hide();
      }
      return;
    }

    if (type === "spend_get") {
      const thresholdPence = normaliseMoneyToPence(promo.threshold);
      const amountOffPence = normaliseMoneyToPence(promo.amountOff);

      if (savings > 0) {
        show(promo.label || `${moneyFromCartJS(amountOffPence)} off applied`, "applied");
      } else if (thresholdPence > 0 && originalTotal < thresholdPence) {
        show(
          `Spend ${moneyFromCartJS(thresholdPence)} for ${promo.label || `${moneyFromCartJS(amountOffPence)} off`}`,
          "progress"
        );
      } else {
        show(promo.label || `${moneyFromCartJS(amountOffPence)} off unlocked`, "available");
      }
      return;
    }

    // 'free_shipping' as a promo type is the same message the nudge element
    // already owns. Hide here so it is never said twice.
    if (type === "free_shipping") return hide();

    hide();
  }

  /* ──────────────────────────────────────────────────────────
     COUNTDOWN

     Prefers window.HobiDrop from the binder template, which computes the real
     UTC instant of the next Friday 19:00 London. The local fallback below
     rebuilds a Date out of a locale string, which is only correct because both
     sides of the subtraction share the same distortion — fine as a backstop,
     not something to rely on.
     ────────────────────────────────────────────────────────── */

  function getNextFriday7pmMs() {
    if (window.HobiDrop && typeof window.HobiDrop.msUntilDrop === "function") {
      try { return window.HobiDrop.msUntilDrop(); } catch (e) { /* fall through */ }
    }

    const now   = new Date();
    const ukStr = now.toLocaleString("en-GB", { timeZone: "Europe/London" });
    const parts = ukStr.split(/[\s,\/\:]+/);

    const ukNow = new Date(
      parseInt(parts[2], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[0], 10),
      parseInt(parts[3], 10),
      parseInt(parts[4], 10),
      parseInt(parts[5] || "0", 10)
    );

    const target = new Date(ukNow);
    target.setHours(19, 0, 0, 0);

    const day = ukNow.getDay();
    let daysUntil = (5 - day + 7) % 7;
    if (daysUntil === 0 && ukNow >= target) daysUntil = 7;

    target.setDate(target.getDate() + daysUntil);
    return Math.max(0, target.getTime() - ukNow.getTime());
  }

  function pad(n) {
    return n < 10 ? `0${n}` : String(n);
  }

  function updateClosesCountdown() {
    const el = $(SELECTOR.counterCloses);
    if (!el) return;

    let ms  = getNextFriday7pmMs();
    let s   = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400); s %= 86400;
    const h = Math.floor(s / 3600);  s %= 3600;
    const m = Math.floor(s / 60);

    el.textContent = `${d}D ${pad(h)}H ${pad(m)}M`;
  }

  function startClosesCountdown() {
    updateClosesCountdown();
    setInterval(updateClosesCountdown, 60000);
  }

  /* ──────────────────────────────────────────────────────────
     TOTALS
     ────────────────────────────────────────────────────────── */

  function updateBinderTotal() {
    const savings    = getSelectedSavings();
    const finalTotal = getSelectedFinalTotal();
    const origTotal  = getSelectedOriginalTotal();

    const finalEl = $(SELECTOR.counterTotalPrice);
    if (finalEl) finalEl.textContent = moneyFromCartJS(finalTotal);

    const origEl = $(SELECTOR.counterOrigPrice);
    if (origEl) {
      if (savings > 0) {
        origEl.textContent   = moneyFromCartJS(origTotal);
        origEl.style.display = "";
      } else {
        origEl.textContent   = "";
        origEl.style.display = "none";
      }
    }
  }

  /* ──────────────────────────────────────────────────────────
     GROUP TABS
     ────────────────────────────────────────────────────────── */

  function updateGroupTabs() {
    $$(SELECTOR.groupTab).forEach((group) => {
      const tiles        = $$(SELECTOR.tile, group);
      const selectAllBtn = $(SELECTOR.groupSelectAll, group);
      const statusEl     = $(SELECTOR.groupStatus, group);
      const remainingEl  = $(SELECTOR.groupRemaining, group);
      const priceEl      = $(SELECTOR.groupPrice, group);

      let totalCount     = 0;
      let availableCount = 0;
      let selectedCount  = 0;
      let selectedTotal  = 0;
      let lowestPrice    = Infinity;

      tiles.forEach((tile) => {
        const vid = tile.getAttribute("data-variant-id");
        const btn = $(SELECTOR.addBtn, tile);
        if (!vid || !btn || btn.tagName !== "BUTTON") return;

        totalCount++;

        if (!btn.classList.contains("is-disabled")) {
          availableCount++;
          const pence = tilePricePence(tile);
          if (pence && pence < lowestPrice) lowestPrice = pence;
        }

        if (isSelected(vid)) {
          selectedCount++;
          selectedTotal += tilePricePence(tile);
        }
      });

      const remaining   = Math.max(0, availableCount - selectedCount);
      const allInStock  = availableCount === totalCount && totalCount > 0;
      const allSelected = selectedCount === availableCount && availableCount > 0;
      const allSoldOut  = availableCount === 0;

      if (remainingEl) remainingEl.textContent = String(remaining);

      /* PRICE + SEPARATOR.

         Two things happen here that are easy to miss:

         1. "sold out" is no longer printed. The SOLD OUT tag to the
            right of this line already says it, and saying it twice
            in one row read as a mistake.

         2. Dropping it leaves the meta line ending in a dangling
            middot, because the separator lives in the Liquid label
            ("available · ") not in the price. So the label is
            rewritten alongside the price: no price, no separator.

         "from" is wrapped in its own span rather than being decided
         here by window.innerWidth, so CSS can hide it on phones. A
         width check in JS would only be correct until the device is
         rotated, since nothing re-renders on resize. */
      const labelEl  = $(".binder-group__label", group);
      const showTotal = allSelected && selectedTotal > 0;
      const isSoldOut = !showTotal && lowestPrice === Infinity;

      if (priceEl) {
        if (showTotal) {
          priceEl.textContent = moneyFromCartJS(selectedTotal);
          priceEl.setAttribute("data-mode", "total");
        } else if (!isSoldOut) {
          priceEl.innerHTML =
            `<span class="binder-group__from">from </span>` +
            escapeHtml(moneyFromCartJS(lowestPrice));
          priceEl.setAttribute("data-mode", "from");
        } else {
          priceEl.textContent = "";
          priceEl.setAttribute("data-mode", "soldout");
        }
      }

      if (labelEl) {
        labelEl.innerHTML = isSoldOut
          ? "&nbsp;available"
          : "&nbsp;available&nbsp;&middot;&nbsp;";
      }

      /* Labels come from LABEL at the top of this file. They are plain words —
         no arrow, no tick. The sticker's own pressed state (section 16 of the
         binder stylesheet) is what signals "already done"; a glyph inside a
         fixed-width tag only pushed the text off centre. */
      if (selectAllBtn) {
        if (allSoldOut) {
          selectAllBtn.style.display = "none";
        } else if (allInStock && !allSelected) {
          selectAllBtn.style.display = "";
          selectAllBtn.textContent   = LABEL.selectAll;
          selectAllBtn.classList.remove("is-selected");
          selectAllBtn.disabled = false;
        } else if (allSelected) {
          selectAllBtn.style.display = "";
          selectAllBtn.textContent   = LABEL.allSelected;
          selectAllBtn.classList.add("is-selected");
          selectAllBtn.disabled = false;
        } else {
          selectAllBtn.style.display = "none";
        }
      }

      if (statusEl) {
        if (allSoldOut) {
          statusEl.textContent   = "Sold out";
          statusEl.style.display = "";
        } else if (!allInStock && !allSelected) {
          statusEl.textContent   = "Some claimed";
          statusEl.style.display = "";
        } else {
          statusEl.style.display = "none";
        }
      }
    });
  }

  /* ──────────────────────────────────────────────────────────
     RENDER
     ────────────────────────────────────────────────────────── */

  function render() {
    for (const tile of $$(SELECTOR.tile)) {
      const vid      = tile.getAttribute("data-variant-id");
      const selected = isSelected(vid);

      tile.classList.toggle("is-selected", selected);

      const btn = $(SELECTOR.addBtn, tile);
      if (btn) {
        const label = $(".binder-add-label", btn);
        if (label) label.textContent = selected ? "Selected" : "Select";
        btn.setAttribute("aria-pressed", selected ? "true" : "false");
      }
    }

    renderTray();
    updateHeaderCounters();
    publishSelection();
  }

  function updateHeaderCounters() {
    const clearBtns     = document.querySelectorAll(SELECTOR.clearAllBtn);
    const checkoutLinks = document.querySelectorAll(SELECTOR.checkoutLink);
    const selectedEls   = document.querySelectorAll(SELECTOR.counterSelected);
    const bottomBar     = $(SELECTOR.bottomBar);

    const selectedCount = getSelectedQty();
    const noneSelected  = selectedCount === 0;

    selectedEls.forEach((el) => { el.textContent = String(selectedCount); });
    clearBtns.forEach((btn) => { btn.disabled = noneSelected; });

    checkoutLinks.forEach((link) => {
      link.classList.toggle("is-disabled", noneSelected);
      link.setAttribute("aria-disabled", noneSelected ? "true" : "false");
      if (noneSelected) {
        link.setAttribute("tabindex", "-1");
      } else {
        link.removeAttribute("tabindex");
      }
    });

    if (bottomBar) {
      bottomBar.classList.toggle("is-visible", selectedCount > 0);
      document.body.classList.toggle("binder-bar-active", selectedCount > 0);
    }

    updateBinderTotal();
    updatePromoText();
    updateShipNudge();
    updateGroupTabs();
  }

  /* ──────────────────────────────────────────────────────────
     TRAY
     Built from local state + tile data so it's instant, rather than waiting
     for the cart round trip to know what's in it.
     ────────────────────────────────────────────────────────── */

  function buildTrayItemHTML(tile) {
    const variantId = tile.getAttribute("data-variant-id");
    const img       = tile.getAttribute("data-image") || "";
    const title     = tile.getAttribute("data-product-title") || "Item";
    const variant   = tile.getAttribute("data-variant-title") || "";
    const price     = moneyFromCartJS(tilePricePence(tile));

    return `
      <li class="binder-tray__item" data-variant-id="${variantId}">
        ${img ? `<img class="binder-tray__thumb" src="${img}" alt="">` : ""}
        <div class="binder-tray__info">
          <div class="binder-tray__item-title">${escapeHtml(title)}</div>
          ${variant ? `<div class="binder-tray__item-variant">${escapeHtml(variant)}</div>` : ""}
          <div class="binder-tray__item-price">${price}</div>
        </div>
        <button class="binder-tray__remove" type="button" aria-label="Remove">✕</button>
      </li>
    `;
  }

  function renderTray() {
    const tray = $(SELECTOR.tray);
    if (!tray) return;

    const trayList     = $(SELECTOR.trayList, tray);
    const trayCount    = $(SELECTOR.trayCount, tray);
    const traySubtotal = $(SELECTOR.traySubtotal, tray);

    const tiles = $$(SELECTOR.tile).filter((t) => isSelected(t.getAttribute("data-variant-id")));

    if (trayList)     trayList.innerHTML     = tiles.map(buildTrayItemHTML).join("");
    if (trayCount)    trayCount.textContent  = String(tiles.length);
    if (traySubtotal) traySubtotal.textContent = moneyFromCartJS(getSelectedFinalTotal());

    tray.classList.toggle("is-visible", tiles.length > 0);
  }

  /* ──────────────────────────────────────────────────────────
     SELECT / REMOVE — all local, all instant
     ────────────────────────────────────────────────────────── */

  function toggleSelection(variantId) {
    const id = String(variantId);
    if (state.soldOut.has(id)) return;

    if (state.selected.has(id)) {
      state.selected.delete(id);
    } else {
      state.selected.add(id);
    }

    render();
    scheduleSync();
  }

  function handleSelectAll(btn) {
    const group = btn.closest(SELECTOR.groupTab);
    if (!group) return;

    const availableTiles = $$(SELECTOR.tile, group).filter(isSelectable);
    if (!availableTiles.length) return;

    const allSelected = availableTiles.every((tile) =>
      isSelected(tile.getAttribute("data-variant-id"))
    );

    availableTiles.forEach((tile) => {
      const id = String(tile.getAttribute("data-variant-id"));
      if (allSelected) state.selected.delete(id);
      else if (!state.soldOut.has(id)) state.selected.add(id);
    });

    render();
    scheduleSync();
  }

  function handleClearAll() {
    if (!state.selected.size) return;
    state.selected.clear();
    render();
    scheduleSync();
  }

  function handleTrayRemoveClick(removeBtn) {
    const li = removeBtn.closest("[data-variant-id]");
    const id = li?.getAttribute("data-variant-id");
    if (!id) return;

    state.selected.delete(String(id));
    render();
    scheduleSync();
  }

  /* On load, reflect whatever is already in the cart so a returning customer
     doesn't see their selection wiped. */
  async function primeFromCart() {
    try {
      state.cart = await fetchCart();
    } catch (e) {
      console.error("[Binder] could not read cart on load:", e);
      return;
    }

    const mine = binderVariantIds();
    (state.cart.items || []).forEach((item) => {
      const id = String(item.variant_id);
      if (item.quantity > 0 && mine.has(id)) state.selected.add(id);
    });

    state.dirty = false;
    render();
  }

  /* ──────────────────────────────────────────────────────────
     EVENTS
     ────────────────────────────────────────────────────────── */

  function bindEvents() {

    document.addEventListener("click", (e) => {
      const btn = e.target.closest(SELECTOR.clearAllBtn);
      if (!btn) return;
      e.preventDefault();
      handleClearAll();
    });

    document.addEventListener("click", (e) => {
      const btn = e.target.closest(SELECTOR.groupSelectAll);
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      handleSelectAll(btn);
    });

    /* CHECKOUT: flush before navigating.
       With a debounced sync there's a window where the customer's last click
       hasn't reached the cart yet. Without this they'd land on checkout missing
       a card — the single worst failure this design could produce.

       If the flush FAILS, state.dirty is still true. Navigating anyway would
       send them to a checkout that is quietly missing cards, which is the exact
       failure this handler exists to prevent — so hold them here, restore the
       link and let them try again. */
    document.addEventListener("click", async (e) => {
      const link = e.target.closest(SELECTOR.checkoutLink);
      if (!link) return;

      if (link.classList.contains("is-disabled")) {
        e.preventDefault();
        return;
      }

      if (state.dirty || state.syncing || state.pending) {
        e.preventDefault();
        link.classList.add("is-loading");
        try {
          await flushSync();
        } finally {
          link.classList.remove("is-loading");
        }

        if (state.dirty) {
          console.error("[Binder] checkout blocked — cart sync did not complete");
          link.classList.add("is-retry");
          setTimeout(() => link.classList.remove("is-retry"), 2000);
          return;
        }

        window.location.href = link.getAttribute("href") || "/checkout";
      }
    });

    document.addEventListener(
      "pointerdown",
      (e) => {
        const btn = e.target.closest(SELECTOR.addBtn);
        if (!btn || btn.tagName !== "BUTTON") return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains("is-disabled")) return;
        toggleSelection(btn.getAttribute("data-variant-id"));
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      "click",
      (e) => {
        const isMobile = window.innerWidth < 768;
        const hit = e.target.closest(
          isMobile
            ? ".card-image-link, .card-image"
            : ".card-image-link, .card-image, .card-meta, .card-price"
        );
        if (!hit) return;
        if (e.target.closest("a, button")) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1) return;

        const tile = hit.closest(SELECTOR.tile);
        if (!tile || !isSelectable(tile)) return;

        e.preventDefault();
        e.stopPropagation();
        toggleSelection(tile.getAttribute("data-variant-id"));
      },
      { capture: true }
    );

    const tray = $(SELECTOR.tray);
    if (tray) {
      const header = $(SELECTOR.trayToggle, tray);
      const body   = $(SELECTOR.trayBody, tray);

      if (header && body) {
        header.addEventListener("click", (e) => {
          if (e.target.closest(".binder-tray__remove")) return;
          body.classList.toggle("is-open");
        });
      }

      tray.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(".binder-tray__remove");
        if (!removeBtn) return;
        e.preventDefault();
        handleTrayRemoveClick(removeBtn);
      });
    }

    /* Flush on tab-away and on unload so a customer who wanders off mid-pick
       still has their cards in the cart when they come back. */
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && state.dirty) flushSync();
    });

    window.addEventListener("pagehide", () => {
      if (state.dirty) flushSync();
    });

    document.addEventListener("click", (e) => {
      const backTop = e.target.closest("[data-binder-backtop]");
      if (!backTop) return;
      e.preventDefault();

      const target = document.querySelector(".binder-container");
      if (!target) return;

      const header = document.querySelector("header, .header, .shopify-section-header");
      const offset = header ? header.offsetHeight + 10 : 10;
      const top    = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top, behavior: "smooth" });
    });
  }

  /* External cart changes (mini-cart, another tab) only matter when we have
     nothing pending — otherwise they'd stomp on the customer's live selection. */
  function listenForExternalCartChanges() {
    const names = [
      "cart:refresh", "cart:updated", "cart:update",
      "theme:cart:refresh", "theme:cart:updated",
      "ajaxCart:refresh", "ajaxCart:updated",
      "cart:change", "cart:render"
    ];

    const handler = async (e) => {
      if (e?.detail?.source === "binder") return;
      if (state.dirty || state.syncing) return;

      // Our own flush makes the theme redraw its mini-cart, and the theme then
      // broadcasts an unflagged refresh of its own. Treating that echo as an
      // external change rebuilds the selection from a cart that may still be
      // catching up — cards blink out and back, and the shipping rule flickers
      // with them.
      if (Date.now() - state.lastSyncAt < EXTERNAL_COOLDOWN_MS) return;

      try {
        const fresh = await fetchCart();

        const mine = binderVariantIds();
        const inCart = new Set(
          (fresh.items || [])
            .filter((i) => i.quantity > 0)
            .map((i) => String(i.variant_id))
        );

        // A genuine external change (mini-cart, another tab) can legitimately
        // remove cards. A stale read cannot be told apart from that by content
        // alone — but if something changed under us WHILE we were mid-flight,
        // the safe move is to keep what the customer can see rather than wipe
        // it from a snapshot we no longer trust.
        if (state.dirty || state.syncing) return;

        state.cart = fresh;
        state.selected = new Set([...inCart].filter((id) => mine.has(id)));
        render();
      } catch (err) {
        console.error("[Binder] external cart sync failed:", err);
      }
    };

    names.forEach((name) => {
      document.addEventListener(name, handler);
      document.documentElement.addEventListener(name, handler);
      window.addEventListener(name, handler);
    });
  }

  /* ──────────────────────────────────────────────────────────
     STOCK POLLING
     ────────────────────────────────────────────────────────── */

  function buildSleeve() {
    const wrap = document.createElement("div");
    wrap.className = "binder-empty-sleeve";
    wrap.setAttribute("aria-hidden", "true");

    const label = document.createElement("span");
    label.className = "binder-empty-label binder-empty-label--single";
    label.setAttribute("data-claimed-label", "");

    const top = document.createElement("span");
    top.className   = "binder-empty-label__top";
    top.textContent = "CLAIMED";

    label.appendChild(top);
    wrap.appendChild(label);
    return wrap;
  }

  /* Returns true when the card was actually deselected, so the caller knows a
     cart sync is owed. */
  function markCardAsSoldOut(variantId) {
    const id   = String(variantId);
    const card = document.querySelector(`.binder-card[data-variant-id="${id}"]`);
    const wasSelected = state.selected.has(id);

    if (card && !card.classList.contains("is-empty")) {
      claimTimes[id] = new Date();

      const img = card.querySelector(".card-image img");
      if (img) img.replaceWith(buildSleeve());

      const price = card.querySelector(".card-price");
      if (price) price.innerHTML = '<span class="binder-soldout">Sold out</span>';

      const btn = card.querySelector(".binder-add[data-variant-id]");
      if (btn && btn.tagName === "BUTTON") {
        const ghost = document.createElement("div");
        ghost.className = "binder-add binder-add--check is-disabled";
        ghost.setAttribute("data-variant-id", id);
        btn.replaceWith(ghost);
      }

      card.classList.add("is-empty");
      card.classList.remove("is-selected");
    }

    state.selected.delete(id);
    state.soldOut.add(id);

    return wasSelected;
  }

  function updateClaimedLabels() {
    const now = new Date();

    document.querySelectorAll(".binder-card[data-variant-id]").forEach((card) => {
      const variantId = card.dataset.variantId;

      let claimedAt = claimTimes[variantId];
      if (!claimedAt) {
        const ts = card.dataset.claimedAt;
        if (!ts || ts === "0") return;
        claimedAt = new Date(parseInt(ts, 10) * 1000);
        claimTimes[variantId] = claimedAt;
      }

      const label = card.querySelector("[data-claimed-label]");
      if (!label) return;

      const diffMs    = now - claimedAt;
      const diffMins  = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays  = Math.floor(diffHours / 24);

      let isDouble   = false;
      let bottomText = "";

      if (diffMins < 1) {
        isDouble = false;
      } else if (diffMins < 60) {
        isDouble   = true;
        bottomText = `${diffMins} ${diffMins === 1 ? "MIN" : "MINS"} AGO`;
      } else if (diffHours < 24) {
        isDouble   = true;
        bottomText = `${diffHours} ${diffHours === 1 ? "HOUR" : "HOURS"} AGO`;
      } else {
        isDouble   = true;
        bottomText = `${diffDays} ${diffDays === 1 ? "DAY" : "DAYS"} AGO`;
      }

      label.className = `binder-empty-label ${isDouble ? "binder-empty-label--double" : "binder-empty-label--single"}`;

      let topEl = label.querySelector(".binder-empty-label__top");
      if (!topEl) {
        topEl           = document.createElement("span");
        topEl.className = "binder-empty-label__top";
        label.appendChild(topEl);
      }
      topEl.textContent = "CLAIMED";

      let bottomEl = label.querySelector(".binder-empty-label__bottom");
      if (isDouble) {
        if (!bottomEl) {
          bottomEl           = document.createElement("span");
          bottomEl.className = "binder-empty-label__bottom";
          label.appendChild(bottomEl);
        }
        bottomEl.textContent = bottomText;
      } else if (bottomEl) {
        bottomEl.remove();
      }
    });
  }

  /* Polling yields to cart syncs and staggers its requests, so it can't
     contribute to a rate limit at the exact moment a customer is selecting. */
  async function pollAllProducts() {
    if (state.syncing || state.dirty) return;
    if (document.hidden) return;

    const groups = document.querySelectorAll(".binder-group[data-product-handle]");
    let changed = false;
    let deselected = false;

    for (const group of groups) {
      if (state.syncing || state.dirty) break;

      const handle = group.dataset.productHandle;
      if (!handle) continue;

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) continue;

        const product = await res.json();

        product.variants.forEach((variant) => {
          const id       = String(variant.id);
          const current  = variant.inventory_quantity;
          const previous = stockState[id];

          /* Trust `available` over the raw number. A variant with inventory
             tracking off reports 0 forever while still being buyable, and
             condemning those would grey out perfectly sellable cards. */
          if (variant.available === true) {
            if (state.soldOut.has(id)) state.soldOut.delete(id);
            stockState[id] = current;
            return;
          }

          if (previous === undefined) { stockState[id] = current; return; }
          if (current === previous) return;
          if (current <= 0 && previous > 0 && variant.available === false) {
            if (markCardAsSoldOut(id)) deselected = true;
            changed = true;
          }

          stockState[id] = current;
        });
      } catch (err) {
        console.warn(`[Binder] poll failed for ${handle}, skipping:`, err);
      }

      await sleep(POLL_STAGGER_MS);
    }

    if (changed) render();

    /* A card that sold out while it was IN the customer's selection has just
       been dropped from state.selected — but it is still sitting in the Shopify
       cart, because Shopify does not release a line when inventory hits zero.
       Without this the binder shows CLAIMED while the customer still pays for
       it at checkout. Sync so the cart matches what is on screen. */
    if (deselected) scheduleSync(0);
  }

  function initPolling() {
    document.querySelectorAll(".binder-card[data-claimed-at]").forEach((card) => {
      const ts = card.dataset.claimedAt;
      if (ts && ts !== "0") {
        claimTimes[card.dataset.variantId] = new Date(parseInt(ts, 10) * 1000);
      }
    });

    pollAllProducts();
    setInterval(pollAllProducts, POLL_INTERVAL_MS);

    updateClaimedLabels();
    setInterval(updateClaimedLabels, 30000);
  }

  /* ──────────────────────────────────────────────────────────
     INIT
     ────────────────────────────────────────────────────────── */

  function init() {
    if (!$(SELECTOR.tile)) return;

    publishSelection();
    bindEvents();
    listenForExternalCartChanges();
    primeFromCart();
    startClosesCountdown();
    initPolling();
  }

  document.addEventListener("DOMContentLoaded", init);
})();